import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, Typography, CircularProgress, Alert, Container } from '@mui/material';
import { useEmailConfirmation } from '../../hooks/useEmailConfirmation';
import { useSearchParams, useNavigate } from 'react-router-dom';

const PasswordSetupPage: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const { confirmEmailAndSetPassword, loading, error } = useEmailConfirmation();

  useEffect(() => {
    if (!token) {
      // Redirect to login if no token present
      navigate('/auth');
    }
  }, [token, navigate]);

  const validatePassword = (password: string) => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long.';
    }
    return '';
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    const passwordValidation = validatePassword(password);
    if (passwordValidation) {
      setPasswordError(passwordValidation);
      return;
    }
    
    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }
    
    setPasswordError('');
    
    if (!token) return;
    
    const result = await confirmEmailAndSetPassword({ token, password });
    
    if (result) {
      setSuccess(true);
      // Redirect to login after success
      setTimeout(() => {
        navigate('/auth');
      }, 2000);
    }
  };

  if (!token) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Alert severity="error">
            Invalid confirmation link. Please check your email and try again.
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 'bold' }}>
          Set Your Password
        </Typography>

        <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
          Your email has been confirmed! Now create a secure password for your account.
        </Typography>

        {/* Success Message */}
        {success && (
          <Alert severity="success" sx={{ mb: 2, width: '100%' }}>
            Account created successfully! Redirecting to login...
          </Alert>
        )}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
          {/* Password Field */}
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="new-password"
            sx={{ mb: 2 }}
            value={password}
            onChange={e => setPassword(e.target.value)}
            error={!!passwordError}
            helperText={passwordError || 'Password must be at least 8 characters long'}
          />

          {/* Confirm Password Field */}
          <TextField
            margin="normal"
            required
            fullWidth
            name="confirmPassword"
            label="Confirm Password"
            type="password"
            id="confirmPassword"
            sx={{ mb: 3 }}
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            error={!!passwordError && passwordError.includes('match')}
          />

          {/* Submit Button */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading || success}
            sx={{
              mt: 2,
              mb: 2,
              py: 1.5,
              fontSize: '1.1rem',
              borderRadius: 8,
              backgroundColor: '#00A3FF',
              '&:hover': {
                backgroundColor: '#0091E6'
              }
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Create Account'}
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default PasswordSetupPage;
